import React, { useState, useEffect } from 'react';
import { LinearProgress, InputAdornment } from '@mui/material';
import { 
  Container, Paper, Typography, Grid, TextField, Avatar, 
  Button, Divider, Box, Tab, Tabs, Card, CardContent, Alert, CircularProgress,
  Link, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton
} from '@mui/material';
import { 
  Save as SaveIcon,
  Visibility as VisibilityIcon, 
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API from './services/api';

// Add this mapping near the top of your file
const ROLE_LABELS = {
  admin: 'Administrator',
  manager: 'Manager',
  FIELD_SUPERVISOR: 'Field Supervisor',
  supervisor: 'Supervisor',
  employee: 'Employee',
  // Add other roles as needed
};

// Password strength indicator component
const PasswordStrengthMeter = ({ password }) => {
  const calculateStrength = (password) => {
    if (!password) return 0;
    
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    
    return strength;
  };

  const strength = calculateStrength(password);
  let color = 'error';
  let label = 'Weak';
  
  if (strength >= 100) {
    color = 'success';
    label = 'Strong';
  } else if (strength >= 50) {
    color = 'warning';
    label = 'Medium';
  }

  return (
    <Box sx={{ width: '100%', mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={strength} 
            color={color}
            sx={{ height: 8, borderRadius: 5 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

// Main Profile component
const Profile = () => {
  const { user, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  
  // Form state - simplified
  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    email: '', // USERNAME in database
    
    // Password
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    
    // Work Info
    jobTitle: '',
    department: '',
    supervisor: '',
    location: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Session data mock
  const [sessions] = useState([
    { id: 1, device: 'Chrome on Windows', ip: '192.168.1.1', lastActive: '2023-03-28 14:30', current: true },
    { id: 2, device: 'Mobile App on Android', ip: '192.168.0.5', lastActive: '2023-03-27 09:15', current: false },
  ]);

  // Activity log mock
  const [activityLog] = useState([
    { id: 1, action: 'Password Changed', date: '2023-03-15 10:45', ip: '192.168.1.1' },
    { id: 2, action: 'Login', date: '2023-03-28 09:30', ip: '192.168.1.1' },
    { id: 3, action: 'Profile Updated', date: '2023-03-10 14:20', ip: '192.168.1.1' },
  ]);

  // Validation state
  const [errors, setErrors] = useState({});

  // Load user data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await API.get('/api/profileDashboard', { withCredentials: true });
        if (response.data.success && response.data.user) {
          const userData = response.data.user;
		  console.log('Profile API userData:', userData); // <-- Add this line
setFormData(prevData => ({
  ...prevData,
  department: userData.DEPARTMENT_NAME || userData.DEPARTMENT || userData.department || '',
  jobTitle: userData.TITLE || userData.jobTitle || '',
  firstName: userData.FIRST_NAME || userData.firstName || '',
  lastName: userData.LAST_NAME || userData.lastName || '',
  email: userData.USERNAME || userData.email || '',
  role: userData.ROLE || userData.role || '',
}));
        }
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
        setErrorMessage('Failed to load profile data. Please try again later.');
        
        // If API fails, still populate with user context data if available
        if (user) {
          setFormData(prevData => ({
            ...prevData,
            email: user.USERNAME || '',
            firstName: user.FIRST_NAME || '',
            lastName: user.LAST_NAME || '',
            jobTitle: user.JOB_TITLE || '',
            department: user.DEPARTMENT || '',
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear messages when changing tabs
    setSuccessMessage('');
    setErrorMessage('');
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Validate the form
  const validateForm = () => {
    const newErrors = {};
    
    // Validate personal info
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate password change
  const validatePasswordChange = () => {
    const errors = {};
    if (!passwordData.currentPassword) errors.currentPassword = 'Current password is required';
    if (!passwordData.newPassword) errors.newPassword = 'New password is required';
    else if (passwordData.newPassword.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    if (passwordData.newPassword !== passwordData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle personal info submission
  const handlePersonalInfoSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setSuccessMessage('');
    setErrorMessage('');
    
    // Validate form data
    if (!validateForm()) {
      setErrorMessage('Please correct the errors in the form');
      return;
    }
    
    try {
      setSaving(true);
      
      const response = await API.put('/api/profileDashboard/personal', {
        firstName: formData.firstName,
        lastName: formData.lastName
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setSuccessMessage('Personal information updated successfully');
        
        // Update user in auth context
        if (refreshUserData) {
          refreshUserData();
        }
      }
    } catch (error) {
      console.error('Failed to update personal info:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to update personal information');
    } finally {
      setSaving(false);
    }
  };

  // Handle work info submission
  const handleWorkInfoSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      setSaving(true);
      
      const response = await API.put('/api/profileDashboard/work', {
        department: formData.department,
        jobTitle: formData.jobTitle
      });
      
      if (response.data.success) {
        setSuccessMessage('Work information updated successfully');
        
        // Update user in auth context
        if (refreshUserData) {
          refreshUserData();
        }
      }
    } catch (error) {
      console.error('Failed to update work info:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to update work information');
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    if (!validatePasswordChange()) return;

    try {
      setSaving(true);
      const response = await API.put('/api/profileDashboard/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      },
      {
        withCredentials: true
      }
    );
      if (response.data.success) {
        setSuccessMessage('Password updated successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setErrorMessage(response.data.message || 'Failed to update password');
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  // Render tabs content
  const renderTabContent = () => {
    switch (tabValue) {
      case 0: // Personal Info
        return (
          <Box sx={{ p: 3 }}>
            <form onSubmit={handlePersonalInfoSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        disabled
                        helperText="Email cannot be changed"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3 }}>
                    <Button 
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Personal Info'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        );
        
      case 1: // Account & Security (Password Change)
        return (
          <Box sx={{ p: 3 }}>
            <form onSubmit={handlePasswordSubmit}>
              <Typography variant="h6" gutterBottom>
                Password Management
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={e => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    error={!!passwordErrors.currentPassword}
                    helperText={passwordErrors.currentPassword}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    error={!!passwordErrors.newPassword}
                    helperText={passwordErrors.newPassword}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {passwordData.newPassword && 
                    <PasswordStrengthMeter password={passwordData.newPassword} />
                  }
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    error={!!passwordErrors.confirmPassword}
                    helperText={passwordErrors.confirmPassword}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? 'Updating...' : 'Update Password'}
                  </Button>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Security Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1">Active Sessions</Typography>
                        <Button 
                          size="small" 
                          onClick={() => setSessionDialogOpen(true)}
                        >
                          View All
                        </Button>
                      </Box>
                      {sessions.filter(s => s.current).map(session => (
                        <Box key={session.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2">{session.device}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last active: {session.lastActive}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            Current Session
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                  
                  {/* Sessions Dialog */}
                  <Dialog 
                    open={sessionDialogOpen} 
                    onClose={() => setSessionDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                  >
                    <DialogTitle>Active Sessions</DialogTitle>
                    <DialogContent>
                      <Box sx={{ p: 1 }}>
                        {sessions.map(session => (
                          <Card key={session.id} variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="body1">{session.device}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    IP: {session.ip}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Last active: {session.lastActive}
                                  </Typography>
                                </Box>
                                <Box>
                                  {session.current ? (
                                    <Typography variant="caption" sx={{ color: 'success.main' }}>
                                      Current Session
                                    </Typography>
                                  ) : (
                                    <Button 
                                      size="small" 
                                      color="error"
                                      variant="outlined"
                                    >
                                      Terminate
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setSessionDialogOpen(false)}>Close</Button>
                      <Button color="error">Sign Out All Other Devices</Button>
                    </DialogActions>
                  </Dialog>
                </Grid>
                
                {/* <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1">Recent Activity</Typography>
                      <Box sx={{ mt: 2 }}>
                        {activityLog.slice(0, 3).map(log => (
                          <Box key={log.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">{log.action}</Typography>
                            <Typography variant="caption" color="text.secondary">{log.date}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid> */}
              </Grid>
            </form>
          </Box>
        );
        
      case 2: // Work Information
        return (
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleWorkInfoSubmit}>
              <Typography variant="h6" gutterBottom>
                Work Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    name="jobTitle"
                    value={formData.jobTitle || ''}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Department"
                    name="department"
                    value={formData.department ? formData.department : 'None Specified'}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Work Info'}
                  </Button>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Role & Permissions
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" fontWeight="bold">Role</Typography>
                      <Typography variant="body2">
                        {ROLE_LABELS[formData.role] || 'Employee'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" fontWeight="bold">Access Level</Typography>
                      <Typography variant="body2">
                        {user?.ROLE === 'admin'
                          ? 'Full System Access'
                          : user?.ROLE === 'manager'
                          ? 'Department Management'
                          : user?.ROLE === 'FIELD_SUPERVISOR'
                          ? 'Field Operations Management'
                          : user?.ROLE === 'supervisor'
                          ? 'Team Supervision'
                          : 'Standard User'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </form>
          </Box>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 0, mb: 3, borderRadius: 2, overflow: 'hidden' }} elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            aria-label="profile settings tabs"
          >
            <Tab icon={<PersonIcon />} label="Personal Info" />
            <Tab icon={<LockOpenIcon />} label="Account & Security" />
            <Tab icon={<WorkIcon />} label="Work Information" />
          </Tabs>
        </Box>
        
        {/* Messages */}
        {successMessage && (
          <Alert severity="success" sx={{ m: 2 }}>{successMessage}</Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ m: 2 }}>{errorMessage}</Alert>
        )}
        
        {/* Tab Content */}
        {renderTabContent()}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
        Need help? Contact <Link href="mailto:support@3p3.ca">H&S Advisor</Link>
      </Typography>
    </Container>
  );
};

export default Profile;